import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

/** Requisitos de segurança da organização do usuário logado (fase 2b, `get_my_security_requirements`). */
export interface SecurityRequirements {
  mfaRequired: boolean;
  inactivityMinutes: number;
}

export const DEFAULT_INACTIVITY_MINUTES = 30;
const ALLOWED_INACTIVITY = [15, 30, 60, 120];

/**
 * Lê os requisitos da org. Sem org ou sem linha de configuração → padrões (sem 2FA obrigatório, 30 min).
 * Lança em erro de rede/RPC; quem chama decide o comportamento (gate é fail-open, inatividade cai no padrão).
 */
export async function fetchSecurityRequirements(): Promise<SecurityRequirements> {
  const { data, error } = await supabase.rpc("get_my_security_requirements");
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : null;
  const minutes = row?.inactivity_minutes;
  return {
    mfaRequired: row?.mfa_required === true,
    inactivityMinutes:
      typeof minutes === "number" && ALLOWED_INACTIVITY.includes(minutes) ? minutes : DEFAULT_INACTIVITY_MINUTES,
  };
}

/** `ok` = segue para o app; `setup` = falta fator TOTP verificado; `verify` = tem fator mas a sessão é aal1. */
export type MfaGateStatus = "ok" | "setup" | "verify";

export const MFA_GATE_QUERY_KEY = "mfa-gate";

async function resolveMfaGate(): Promise<MfaGateStatus> {
  const requirements = await fetchSecurityRequirements();
  if (!requirements.mfaRequired) return "ok";

  const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
  if (factorsError) throw factorsError;
  const hasVerifiedTotp = factors.totp.some((factor) => factor.status === "verified");
  if (!hasVerifiedTotp) return "setup";

  const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalError) throw aalError;
  return aal.currentLevel === "aal2" ? "ok" : "verify";
}

/**
 * Gate de 2FA obrigatório (R1, C5) usado no `AppRoute`. Erro de leitura → `ok` (fail-open no app, para não travar
 * todos os usuários) com registro no Sentry sem dados pessoais. A barreira de banco (aal2 na RLS) é fase futura.
 */
export function useMfaGate(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: [MFA_GATE_QUERY_KEY, userId],
    enabled: enabled && !!userId,
    staleTime: 60_000,
    // Status bloqueante em cache (ex.: acabou de verificar o 2FA no /login) é sempre relido ao montar.
    refetchOnMount: (query) => (query.state.data === "ok" ? true : "always"),
    retry: 1,
    queryFn: async (): Promise<MfaGateStatus> => {
      try {
        return await resolveMfaGate();
      } catch (error: unknown) {
        Sentry.captureException(error instanceof Error ? error : new Error("mfa_gate_read_failed"), {
          tags: { feature: "mfa-gate" },
        });
        return "ok";
      }
    },
  });
}
